/**
 * SpotlightCard Component
 * 
 * A card with a cursor-following gold spotlight effect.
 * When the cursor moves over the card, a radial gradient "spotlight"
 * follows it, making the UI feel alive and reactive.
 * 
 * Usage: <SpotlightCard>Content here</SpotlightCard>
 */

import { useRef, useState, useCallback, type ReactNode, type MouseEvent } from 'react';
import { cn } from '@/lib/utils';

interface SpotlightCardProps {
  children: ReactNode;
  className?: string;
  /** Additional classes for the inner content wrapper */
  contentClassName?: string;
  /** Whether to show the spotlight border glow */
  borderGlow?: boolean;
}

export function SpotlightCard({
  children,
  className,
  contentClassName,
  borderGlow = true,
}: SpotlightCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setSpotlight({ x, y });
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setSpotlight({ x: 50, y: 50 });
  }, []);

  return (
    <div
      ref={cardRef}
      className={cn(
        'relative overflow-hidden rounded-lg',
        'bg-verdict-surface-glass backdrop-blur-md',
        'border border-border-subtle',
        'transition-all duration-300',
        borderGlow && isHovered && 'border-verdict-primary/15',
        className
      )}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        '--spotlight-x': `${spotlight.x}%`,
        '--spotlight-y': `${spotlight.y}%`,
      } as React.CSSProperties}
    >
      {/* Spotlight gradient overlay */}
      <div
        className={cn(
          'absolute inset-0 pointer-events-none transition-opacity duration-300',
          isHovered ? 'opacity-100' : 'opacity-0'
        )}
        style={{
          background: `radial-gradient(
            circle 250px at ${spotlight.x}% ${spotlight.y}%,
            rgba(212, 165, 116, 0.06) 0%,
            transparent 70%
          )`,
        }}
      />

      {/* Content */}
      <div className={cn('relative z-10', contentClassName)}>{children}</div>
    </div>
  );
}

